import { useEffect, useState } from 'react'
import { Pressable, Text } from 'react-native'
import { useTranslation } from 'react-i18next'

import { cn } from '@/utils/cn'
import { useCountdown } from '@/hooks/useCountdown'
import { getAuthErrorCode, getAuthErrorStatus } from '@/stores/auth'
import { useToast } from '@/components/base'
import { FormInput } from '@/components/form-control/FormInput'
import { useLoadingIndicator } from '@/components/LoadingIndicator'

export function OtpInput({
  control,
  onSubmit,
  requestOTP,
  automaticallyStartTimer = false,
  isButtonHidden = false
}: {
  control: any
  onSubmit: () => void
  requestOTP: () => Promise<unknown> | void
  automaticallyStartTimer?: boolean
  isButtonHidden?: boolean
}) {
  const { t } = useTranslation()
  const toast = useToast()
  const loading = useLoadingIndicator()
  const { countdown, startTimer } = useCountdown({ startTime: 60 })
  const isCounting = countdown > 0
  const [isHidden, setIsHidden] = useState(isButtonHidden)

  const handleSend = async () => {
    setIsHidden(true)
    loading.start()
    try {
      await requestOTP()
      toast.show({ variant: 'success', description: t('auth:toast.success.sendVerificationCode') })
      startTimer()
    } catch (e: any) {
      const errorCode = getAuthErrorCode(e)
      const status = getAuthErrorStatus(e)
      if (errorCode === 3006 || errorCode === 1023) {
        toast.show({ variant: 'error', description: t('auth:toast.error.sendVerificationCodeTooFast') })
      } else if (
        errorCode === 2021 ||
        errorCode === 2023 ||
        errorCode === 1024 ||
        errorCode === 1025 ||
        status === 429
      ) {
        // 1024 is retained here for compatibility with servers deployed before
        // OTP issuance and verification limits received separate API codes.
        toast.show({ variant: 'error', description: t('auth:toast.error.sendVerificationCodeQuotaReached') })
      } else if (errorCode === 1022) {
        toast.show({ variant: 'error', description: t('auth:toast.error.smsServiceUnavailable') })
      } else if (errorCode === 1042 || errorCode === 1040) {
        toast.show({ variant: 'error', description: t('auth:toast.error.notRegisteredPhoneNumber') })
      } else {
        toast.show({ variant: 'error', description: t('auth:toast.error.sendVerificationCode') })
      }
      setIsHidden(false)
    } finally {
      loading.stop()
    }
  }

  useEffect(() => {
    if (automaticallyStartTimer) {
      startTimer()
    }
  }, [])

  return (
    <FormInput
      control={control}
      name={'verifyCode'}
      label={t('auth:label.verificationCode')}
      placeholder={t('auth:placeholder.verificationCode')}
      textContentType={'oneTimeCode'}
      inputMode={'numeric'}
      keyboardType={'number-pad'}
      autoCapitalize={'none'}
      autoCorrect={false}
      returnKeyType={'go'}
      rightElement={
        isHidden ? undefined : (
          <Pressable
            accessibilityLabel={t('auth:getVerificationCode')}
            accessibilityRole={'button'}
            accessibilityState={{ disabled: isCounting }}
            onPress={handleSend}
            disabled={isCounting}
          >
            {({ pressed }) =>
              isCounting ? (
                <Text className={'text-sm text-text-info/30'}>{t('auth:retryAfterSeconds', { count: countdown })}</Text>
              ) : (
                <Text className={cn('text-sm text-text-info', pressed && 'text-text-info/50')}>
                  {t('auth:getVerificationCode')}
                </Text>
              )
            }
          </Pressable>
        )
      }
      onSubmitEditing={onSubmit}
      maxLength={6}
    />
  )
}
